import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Icon, Badge, Menu, Dropdown, Breadcrumb, Tooltip, Popover } from 'antd';
import styled from 'styled-components';
import { useNotification } from '~/context/NotificationContext';
import { useTopBar } from '~/context/TopBarContext';
import { FaWhatsapp } from 'react-icons/fa';
import { route } from '~//_shared/components/MainMenu/routes';
import uid from 'uid';
import NotificationDrawer from '~/_shared/components/NotificationCenter/NotificationDrawer';

var BAR_HEIGHT = 72;

/* ─── Single-row top bar ─── */

var Bar = styled.div`
  position: fixed;
  top: 0;
  left: 88px;
  right: 0;
  height: ${BAR_HEIGHT}px;
  background: #fff;
  border-bottom: 1px solid #e8e8e8;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  display: flex;
  align-items: center;
  padding: 0 12px;
  z-index: 99;
  gap: 10px;
`;

var BarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex-shrink: 0;
`;

var BreadcrumbAndTitle = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: 1.2;

  .ant-breadcrumb {
    font-size: 12px;
    line-height: 1.3;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .ant-breadcrumb-separator {
    margin: 0 4px;
  }
`;

var PageTitle = styled.span`
  font-size: 22px;
  font-weight: 600;
  color: #1f2937;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 300px;
`;

var Divider = styled.div`
  width: 1px;
  height: 32px;
  background: #e5e7eb;
  flex-shrink: 0;
`;

/* Page-specific content area (search, filters, center, actions) */
var PageContent = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
  justify-content: center;

  > * + * { margin-left: 0; }
  > input { max-width: 220px; }
  > .ant-calendar-picker { min-width: 140px; }
`;

/* Global search + icons */
var GlobalSection = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
  margin-left: auto;
`;

var SearchToggle = styled.div`
  position: relative;
`;

var SearchInput = styled.input`
  width: ${function(props) { return props.expanded ? '240px' : '0px'; }};
  height: 36px;
  padding: ${function(props) { return props.expanded ? '0 12px 0 32px' : '0'; }};
  border-radius: 8px;
  border: ${function(props) { return props.expanded ? '1px solid #e5e7eb' : 'none'; }};
  background: ${function(props) { return props.expanded ? '#f9fafb' : 'transparent'; }};
  color: #1f2937;
  font-size: 14px;
  outline: none;
  transition: all 0.2s ease;
  opacity: ${function(props) { return props.expanded ? 1 : 0; }};

  &::placeholder { color: #9ca3af; }
  &:focus {
    border-color: #8b5cf6;
    background: #fff;
    box-shadow: 0 0 0 2px rgba(139, 92, 246, 0.1);
  }
`;

var SearchIconBtn = styled.div`
  position: ${function(props) { return props.expanded ? 'absolute' : 'relative'; }};
  left: ${function(props) { return props.expanded ? '6px' : '0'; }};
  top: ${function(props) { return props.expanded ? '50%' : '0'; }};
  transform: ${function(props) { return props.expanded ? 'translateY(-50%)' : 'none'; }};
  width: ${function(props) { return props.expanded ? 'auto' : '32px'; }};
  height: ${function(props) { return props.expanded ? 'auto' : '32px'; }};
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #9ca3af;
  font-size: 14px;
  pointer-events: ${function(props) { return props.expanded ? 'none' : 'auto'; }};
  z-index: 1;

  &:hover {
    background: ${function(props) { return props.expanded ? 'none' : '#f3f4f6'; }};
    color: #374151;
  }
`;

var IconButton = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #6b7280;
  transition: all 0.15s ease;
  position: relative;
  flex-shrink: 0;

  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
`;

var AvatarButton = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: ${function(props) { return props.color || '#722ed1'; }};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 13px;
  text-transform: uppercase;
  cursor: pointer;
  margin-left: 2px;
  transition: all 0.15s ease;
  user-select: none;
  flex-shrink: 0;

  &:hover {
    opacity: 0.85;
    box-shadow: 0 0 0 2px rgba(139, 92, 246, 0.3);
  }
`;

/* ─── Search dropdown ─── */

var SearchResultsDropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  width: 320px;
  margin-top: 4px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  max-height: 320px;
  overflow-y: auto;
  z-index: 1002;
`;

var SearchResultItem = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  cursor: pointer;
  color: #374151;
  font-size: 13px;
  transition: background 0.1s ease;

  &:hover { background: #f5f3ff; }
  i { margin-right: 10px; font-size: 15px; color: #8b5cf6; }
`;

var NoResults = styled.div`
  padding: 12px 16px;
  color: #9ca3af;
  font-size: 13px;
`;

var RecentLabel = styled.div`
  padding: 8px 12px 4px;
  font-size: 10px;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

var RECENT_PAGES_KEY = 'contlabs_recent_pages';
var MAX_RECENT = 5;

var getRecentPages = function() {
  try {
    var data = localStorage.getItem(RECENT_PAGES_KEY);
    return data ? JSON.parse(data) : [];
  } catch (e) { return []; }
};

var saveRecentPage = function(item) {
  if (!item || !item.link || !item.title) return;
  try {
    var pages = getRecentPages();
    pages = pages.filter(function(p) { return p.link !== item.link; });
    pages.unshift({ key: item.key, title: item.title, icon: item.icon, link: item.link, visitedAt: new Date().toISOString() });
    if (pages.length > MAX_RECENT) pages = pages.slice(0, MAX_RECENT);
    localStorage.setItem(RECENT_PAGES_KEY, JSON.stringify(pages));
  } catch (e) { /* ignore */ }
};

var formatTimeAgo = function(isoString) {
  if (!isoString) return '';
  var now = Date.now();
  var then = new Date(isoString).getTime();
  var diff = Math.floor((now - then) / 1000);
  if (diff < 60) return 'agora';
  if (diff < 3600) return Math.floor(diff / 60) + 'min';
  if (diff < 86400) return Math.floor(diff / 3600) + 'h';
  if (diff < 604800) return Math.floor(diff / 86400) + 'd';
  return Math.floor(diff / 604800) + 'sem';
};

// Filter out Input/search elements from page content (they're handled by unified search)
var filterOutSearch = function(element) {
  if (!element) return null;
  // If it's an Input element directly
  if (element.type && (element.type === 'input' || element.type.displayName === 'Input' ||
      (element.props && element.props.prefix && element.props.placeholder))) {
    return null;
  }
  // If it's a Fragment or array, filter children
  if (element.props && element.props.children) {
    var children = React.Children.toArray(element.props.children);
    var filtered = children.filter(function(child) {
      if (!child || !child.type) return true;
      // Skip ant Input components
      if (child.props && child.props.placeholder && (
        child.props.placeholder === 'Pesquisar' ||
        child.props.placeholder === 'Pesquisar...' ||
        child.props.placeholder === 'Buscar...' ||
        child.props.placeholder === 'Buscar'
      )) return false;
      // Skip inputs with search prefix icon
      if (child.props && child.props.prefix) return false;
      return true;
    });
    if (filtered.length === 0) return null;
    if (filtered.length === children.length) return element;
    return React.cloneElement(element, {}, filtered);
  }
  return element;
};

var getInitials = function(name) {
  if (!name) return '';
  var names = name.split(' ');
  if (names.length === 1) return names[0].charAt(0);
  return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
};

var getAvatarColor = function(name) {
  var colors = ['#722ed1', '#1890ff', '#52c41a', '#fa8c16', '#eb2f96', '#13c2c2'];
  var index = name.split('').reduce(function(acc, char) { return acc + char.charCodeAt(0); }, 0);
  return colors[index % colors.length];
};

var TopBar = function(props) {
  var user = props.user;
  var searchTerm = props.searchTerm;
  var setSearchTerm = props.setSearchTerm;
  var filteredItems = props.filteredItems;
  var onSearchItemClick = props.onSearchItemClick;
  var isIssueWidgetReady = props.isIssueWidgetReady;
  var topOffset = props.topOffset || 0;

  var topBarCtx = useTopBar();
  var hc = topBarCtx.headerContent;

  var context = useNotification();
  var notifications = context.notifications;
  var unreadCount = notifications.filter(function(n) { return !n.read; }).length;

  var initials = getInitials(user.name);
  var avatarColor = getAvatarColor(user.name);

  var notifDrawerState = useState(false);
  var isNotifDrawerOpen = notifDrawerState[0];
  var setNotifDrawerOpen = notifDrawerState[1];

  var searchExpandedState = useState(false);
  var isSearchExpanded = searchExpandedState[0];
  var setSearchExpanded = searchExpandedState[1];

  var hasSearchTerm = searchTerm && searchTerm.trim().length > 0;
  var recentPages = getRecentPages();
  var showDropdown = isSearchExpanded && (hasSearchTerm || recentPages.length > 0);

  var userMenu = React.createElement(Menu, { style: { minWidth: 200 } },
    React.createElement(Menu.Item, { key: "notifications", onClick: function() { setNotifDrawerOpen(true); } },
      React.createElement('span', null,
        React.createElement(Icon, { type: "bell", style: { marginRight: 8 } }),
        "Notificações",
        unreadCount > 0 ? React.createElement(Badge, { count: unreadCount, style: { marginLeft: 8 } }) : null
      )
    ),
    React.createElement(Menu.Item, { key: "/configuracoes" },
      React.createElement(Link, { to: "/configuracoes" },
        React.createElement(Icon, { type: "setting", style: { marginRight: 8 } }), "Configurações"
      )
    ),
    React.createElement(Menu.Item, { key: "/whatsapp-support" },
      React.createElement("a", { target: "_blank", rel: "noopener noreferrer", href: "https://api.whatsapp.com/send?phone=5548991191788" },
        React.createElement(FaWhatsapp, { style: { marginRight: 8 } }), "Suporte"
      )
    ),
    React.createElement(Menu.Divider, null),
    React.createElement(Menu.Item, { key: "/sair" },
      React.createElement(Link, { to: "/sair" },
        React.createElement(Icon, { type: "logout", style: { marginRight: 8 } }), "Sair"
      )
    )
  );

  var openIssueWidget = function() {
    if (window.IssueWidgetAPI && window.IssueWidgetAPI.open) {
      window.IssueWidgetAPI.open();
    }
  };

  // Build breadcrumbs
  var breadcrumbEl = null;
  if (hc && hc.breadcrumbs) {
    var bcItems = [];
    bcItems.push(
      React.createElement(Breadcrumb.Item, { key: 'home' },
        hc.isPartner ? React.createElement(Tooltip, { title: hc.allowedUsersName },
          React.createElement(Icon, { type: 'lock', style: { marginRight: 4, fontSize: 10 } })
        ) : null,
        React.createElement('span', {
          onClick: hc.handleInicioClick,
          style: { cursor: 'pointer', color: '#8b5cf6' }
        }, 'Início')
      )
    );
    hc.breadcrumbs.forEach(function(item) {
      var label = typeof item === 'object' && item !== null && item.label ? item.label : item;
      var onClick = typeof item === 'object' && item !== null ? item.onClick : null;
      bcItems.push(
        React.createElement(Breadcrumb.Item, { key: uid() },
          React.createElement('span', {
            onClick: onClick,
            style: { cursor: onClick ? 'pointer' : 'default', color: onClick ? '#8b5cf6' : '#9ca3af' }
          }, label)
        )
      );
    });
    breadcrumbEl = React.createElement(Breadcrumb, null, bcItems);
  }

  var hasPageContent = hc && (hc.center || hc.actions || hc.advanced);
  var hasPageSearch = hc && hc.hasSearch;

  // Unified search state
  var unifiedState = useState('');
  var unifiedTerm = unifiedState[0];
  var setUnifiedTerm = unifiedState[1];

  var unifiedFocusState = useState(false);
  var isUnifiedFocused = unifiedFocusState[0];
  var setUnifiedFocused = unifiedFocusState[1];

  var handleUnifiedChange = function(e) {
    var val = e.target.value;
    setUnifiedTerm(val);
    // Update page search directly via handler ref
    if (topBarCtx.pageSearchHandler && topBarCtx.pageSearchHandler.current) {
      topBarCtx.pageSearchHandler.current({ target: { value: val } });
    }
    // Update nav search
    setSearchTerm(val);
  };

  var handleUnifiedBlur = function() {
    setTimeout(function() { setUnifiedFocused(false); }, 200);
  };

  var handleNavItemClick = function(item) {
    saveRecentPage(item);
    onSearchItemClick(item);
    setUnifiedTerm('');
    if (hasPageSearch) {
      topBarCtx.setPageSearchTerm('');
    }
  };

  var hasUnifiedTerm = unifiedTerm && unifiedTerm.trim().length > 0;
  var showNavResults = isUnifiedFocused && hasUnifiedTerm && filteredItems && filteredItems.length > 0;
  var showRecent = isUnifiedFocused && !hasUnifiedTerm && recentPages.length > 0;

  var searchPlaceholder = hasPageSearch ? 'Pesquisar...' : 'Buscar página...';

  var barStyle = topOffset ? { top: topOffset } : {};
  if (hc && hc.advanced) {
    barStyle.borderBottom = 'none';
    barStyle.boxShadow = 'none';
  }
  var barElement = React.createElement(Bar, { style: barStyle },
    // Left: breadcrumbs + title
    React.createElement(BarLeft, null,
      React.createElement(BreadcrumbAndTitle, null,
        breadcrumbEl,
        hc && hc.title && React.createElement(PageTitle, null, hc.title)
      )
    ),

    // Divider
    (hasPageContent || hasPageSearch) && React.createElement(Divider, null),

    // Center: unified search + page content
    React.createElement(PageContent, null,
      // Unified search field
      React.createElement(SearchToggle, { style: { flex: '0 0 auto' } },
        React.createElement(SearchIconBtn, { expanded: true },
          React.createElement(Icon, { type: 'search' })
        ),
        React.createElement(SearchInput, {
          expanded: true,
          placeholder: searchPlaceholder,
          value: unifiedTerm,
          onChange: handleUnifiedChange,
          onFocus: function() { setUnifiedFocused(true); },
          onBlur: handleUnifiedBlur
        }),
        // Nav results dropdown (only when matching pages found)
        showNavResults && React.createElement(SearchResultsDropdown, null,
          React.createElement(RecentLabel, null, 'Ir para página'),
          filteredItems.slice(0, 5).map(function(item) {
            return React.createElement(SearchResultItem, {
              key: item.key,
              onClick: function() { handleNavItemClick(item); }
            },
              item.iconImg ? item.iconImg : React.createElement(Icon, { type: item.icon || 'file' }),
              React.createElement('span', null, item.title)
            );
          })
        ),
        // Recent pages (on focus, no term)
        showRecent && React.createElement(SearchResultsDropdown, null,
          React.createElement(RecentLabel, null, 'Acessados recentemente'),
          recentPages.map(function(page) {
            return React.createElement(SearchResultItem, {
              key: 'recent-' + page.link,
              onClick: function() { handleNavItemClick(page); }
            },
              React.createElement(Icon, { type: page.icon || 'clock-circle' }),
              React.createElement('span', { style: { flex: 1 } }, page.title),
              page.visitedAt && React.createElement('span', { style: { fontSize: 11, color: '#bfbfbf', marginLeft: 8, flexShrink: 0 } }, formatTimeAgo(page.visitedAt))
            );
          })
        )
      ),
      // Page-specific content (center, actions) — search inputs filtered out
      hc && hc.center && filterOutSearch(hc.center),
      hc && hc.actions && hc.actions
    ),

    // Right: icons + avatar
    React.createElement(GlobalSection, null,
      isIssueWidgetReady && React.createElement(IconButton, { onClick: openIssueWidget, title: 'Feedback' },
        React.createElement(Icon, { type: 'message' })
      ),
      React.createElement(IconButton, { onClick: function() { setNotifDrawerOpen(true); }, title: 'Notificações' },
        React.createElement(Badge, { count: unreadCount, offset: [2, -2], style: { fontSize: 10, minWidth: 16, height: 16, lineHeight: '16px', padding: '0 4px' } },
          React.createElement(Icon, { type: 'bell', style: { fontSize: 18, color: '#6b7280' } })
        )
      ),
      React.createElement(IconButton, null,
        React.createElement(Link, { to: '/configuracoes', style: { color: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' } },
          React.createElement(Icon, { type: 'setting', style: { fontSize: 18 } })
        )
      ),
      React.createElement(Dropdown, {
        overlay: userMenu,
        trigger: ['click'],
        placement: 'bottomRight'
      },
        React.createElement(AvatarButton, { color: avatarColor, title: user.name }, initials)
      )
    )
  );

  var drawerEl = React.createElement(NotificationDrawer, {
    visible: isNotifDrawerOpen,
    onClose: function() { setNotifDrawerOpen(false); }
  });

  return React.createElement(React.Fragment, null,
    barElement,
    hc && hc.advanced && React.createElement('div', {
      style: {
        position: 'fixed',
        top: BAR_HEIGHT + topOffset - 1,
        left: 88,
        right: 0,
        height: 36,
        background: '#fafafa',
        borderTop: '1px solid #f0f0f0',
        borderBottom: '1px solid #f0f0f0',
        display: 'flex',
        alignItems: 'center',
        padding: '0 12px',
        zIndex: 98,
        gap: 8,
      }
    }, hc.advanced),
    drawerEl
  );
};

export default TopBar;
export { BAR_HEIGHT };
